/**
 * Reads the download file name out of a Content-Disposition header.
 *
 * The API sends both parameters: `filename=` carries a transliterated ASCII name for older
 * clients, `filename*=UTF-8''...` carries the real (possibly Cyrillic) one. RFC 5987 wins
 * when present.
 *
 * Note: reading this header cross-origin requires the server to expose it via CORS
 * (`WithExposedHeaders("Content-Disposition")`), which it does. `fallback` covers the case
 * where it still comes back undefined - e.g. behind a proxy that strips it.
 */
export const parseContentDispositionFilename = (
  contentDisposition: string | undefined,
  fallback: string
): string => {
  if (!contentDisposition) return fallback;

  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(contentDisposition);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      // malformed percent-encoding - fall through to the plain parameter
    }
  }

  const plainMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(contentDisposition);
  if (plainMatch?.[1]) return plainMatch[1].trim();

  return fallback;
};

/** Saves a Blob to disk via a temporary anchor, cleaning up the object URL afterwards. */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName;
  link.style.display = "none";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  window.URL.revokeObjectURL(url);
};
